import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaShieldAlt,
  FaQrcode,
  FaTimes,
  FaCamera,
  FaCar,
  FaSpinner,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../../auth/AuthContext';
import { warrantyService } from '../../services/apiService';

const TABS = [
  { id: 'active', label: 'Active' },
  { id: 'expiring_soon', label: 'Expiring Soon' },
  { id: 'expired', label: 'Expired' },
  { id: 'all', label: 'All' },
];

const ISSUE_OPTIONS = [
  { value: 'wrong_part', label: 'Wrong part' },
  { value: 'damaged', label: 'Damaged / defective' },
  { value: 'mismatch', label: 'Does not match description' },
  { value: 'fitment', label: 'Fitment issue' },
  { value: 'missing', label: 'Missing parts' },
  { value: 'other', label: 'Other' },
];

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function formatDate(date) {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function statusChipClass(status) {
  switch (status) {
    case 'expiring_soon':
      return 'bg-amber-100 text-amber-900 border-amber-200';
    case 'claimed':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'expired':
      return 'bg-gray-100 text-gray-500 border-gray-200';
    case 'voided':
      return 'bg-gray-100 text-gray-400 border-gray-200';
    default:
      return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  }
}

function statusLabel(status) {
  const labels = {
    active: 'Active',
    expiring_soon: 'Expiring soon',
    claimed: 'Claimed',
    expired: 'Expired',
    voided: 'Voided',
  };
  return labels[status] || status;
}

function WarrantyQR({ warrantyId, size = 160 }) {
  const src = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(
    warrantyId
  )}`;
  return (
    <img
      src={src}
      width={size}
      height={size}
      alt={`Warranty QR ${warrantyId}`}
      className="rounded-lg bg-white p-2"
    />
  );
}

/**
 * B-10C — Digital warranty cards
 */
const WarrantyCards = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('active');
  const [loadError, setLoadError] = useState('');
  const [warranties, setWarranties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qrWarranty, setQrWarranty] = useState(null);
  const [claimWarranty, setClaimWarranty] = useState(null);
  const [claimForm, setClaimForm] = useState({ issueType: '', description: '', photos: [] });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setLoadError('');
      const list = await warrantyService.listWarranties(tab);
      setWarranties(list);
    } catch (err) {
      const msg = err.message || 'Failed to load warranties';
      setLoadError(msg);
      toast.error(msg);
      setWarranties([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const openClaim = (warranty) => {
    setClaimWarranty(warranty);
    setClaimForm({ issueType: '', description: '', photos: [] });
  };

  const handlePhotoPick = async (fileList) => {
    const files = Array.from(fileList || []).slice(0, 5);
    const current = claimForm.photos || [];
    const room = 5 - current.length;
    if (room <= 0) {
      toast.warn('Maximum 5 photos');
      return;
    }
    const encoded = [];
    for (const file of files.slice(0, room)) {
      encoded.push(await readFileAsDataUrl(file));
    }
    setClaimForm((f) => ({ ...f, photos: [...current, ...encoded] }));
  };

  const canSubmitClaim =
    claimForm.issueType &&
    (claimForm.description || '').trim().length >= 20 &&
    (claimForm.photos || []).length >= 1;

  const submitClaim = async () => {
    if (!claimWarranty || !canSubmitClaim) return;
    setSubmitting(true);
    try {
      await warrantyService.raiseClaim(claimWarranty.warrantyId, {
        issueType: claimForm.issueType,
        description: claimForm.description.trim(),
        photos: claimForm.photos,
      });
      toast.success('Warranty claim submitted');
      setClaimWarranty(null);
      await load();
    } catch (err) {
      toast.error(err.message || 'Could not submit claim');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 px-4 py-8 sm:px-6">
      <div className="mx-auto max-w-2xl">
        <Link
          to="/account"
          className="mb-4 inline-flex text-sm font-medium text-blue-600 hover:text-blue-700"
        >
          ← Back to account
        </Link>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FaShieldAlt className="text-red-500" />
          Warranty cards
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Digital warranties for parts and garage work
        </p>

        <div className="mt-6 flex flex-wrap gap-2 rounded-xl border border-gray-200 bg-white p-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`flex-1 min-w-[80px] rounded-lg px-3 py-2 text-xs sm:text-sm font-semibold transition-colors ${
                tab === t.id
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16 text-gray-400">
            <FaSpinner className="animate-spin text-2xl" />
          </div>
        ) : warranties.length === 0 ? (
          <div className="mt-8 rounded-xl border border-gray-200 bg-white p-10 text-center">
            <FaShieldAlt className="mx-auto text-4xl text-gray-300" />
            <p className="mt-3 font-medium text-gray-700">No warranty cards in this tab.</p>
            <p className="mt-1 text-sm text-gray-500">
              Warranties are created when an order reaches <strong>Completed</strong> for this
              account.
            </p>
            {loadError && (
              <p className="mt-3 text-sm text-red-600">{loadError}</p>
            )}
            {user?.phone && (
              <p className="mt-2 text-xs text-gray-400">
                Signed in as …{String(user.phone).slice(-4)}
              </p>
            )}
          </div>
        ) : (
          <ul className="mt-6 space-y-4">
            {warranties.map((w) => {
              const isExpired = w.status === 'expired';
              const isExpiring = w.status === 'expiring_soon';
              const canClaim = w.canRaiseClaim && w.status !== 'claimed';

              return (
                <li
                  key={w.warrantyId}
                  className={`rounded-xl border bg-white p-4 shadow-sm ${
                    isExpiring ? 'border-l-4 border-l-amber-500 border-amber-200' : 'border-gray-200'
                  } ${isExpired ? 'opacity-70 grayscale-[0.3]' : ''}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className={`font-semibold ${isExpired ? 'text-gray-500' : 'text-gray-900'}`}>
                        {w.partName}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                          {w.grade}
                        </span>
                        <span
                          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusChipClass(
                            w.status
                          )}`}
                        >
                          {statusLabel(w.status)}
                        </span>
                        {w.vehicleLabel && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-800">
                            <FaCar className="text-[8px]" />
                            {w.vehicleLabel}
                          </span>
                        )}
                      </div>
                      <p className="mt-2 text-xs text-gray-500">
                        Expires {formatDate(w.expiresAt)}
                        {w.warrantyPeriod ? ` · ${w.warrantyPeriod} day warranty` : ''}
                      </p>
                      {isExpiring && w.daysLeft != null && (
                        <p className="mt-1 text-sm font-semibold text-amber-700">
                          Expires in {w.daysLeft} day{w.daysLeft === 1 ? '' : 's'}.
                        </p>
                      )}
                      {w.faultCounter > 0 && (
                        <p className="mt-2 text-xs text-gray-500 italic">
                          {w.faultCounter} previous claim{w.faultCounter === 1 ? '' : 's'} on this
                          part type from this seller.
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setQrWarranty(w)}
                      className="shrink-0 p-2 rounded-lg border border-gray-200 text-gray-600 hover:border-blue-400 hover:text-blue-600"
                      aria-label="View QR code"
                    >
                      <FaQrcode />
                    </button>
                  </div>

                  {!isExpired && w.status !== 'claimed' && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={!canClaim}
                        onClick={() => canClaim && openClaim(w)}
                        className="text-xs font-semibold px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Raise Warranty Claim
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {qrWarranty && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setQrWarranty(null)}
        >
          <div
            className="bg-white rounded-2xl p-8 max-w-sm w-full text-center relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setQrWarranty(null)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
              aria-label="Close"
            >
              <FaTimes />
            </button>
            <p className="text-sm font-medium text-gray-500 mb-1">Warranty verification</p>
            <p className="font-mono text-xs text-gray-800 mb-4 break-all">{qrWarranty.warrantyId}</p>
            <div className="flex justify-center">
              <WarrantyQR warrantyId={qrWarranty.warrantyId} size={256} />
            </div>
            <p className="mt-4 text-xs text-gray-500">Show this QR at the garage for verification.</p>
          </div>
        </div>
      )}

      {claimWarranty && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 relative max-h-[90vh] overflow-y-auto">
            <button
              type="button"
              onClick={() => setClaimWarranty(null)}
              className="absolute top-3 right-3 text-gray-500"
              aria-label="Close"
            >
              <FaTimes />
            </button>
            <h2 className="text-lg font-bold text-gray-900">Raise warranty claim</h2>
            <p className="text-sm text-gray-600 mt-1">{claimWarranty.partName}</p>
            <p className="text-xs font-mono text-gray-400 mt-1">{claimWarranty.warrantyId}</p>

            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Issue type</label>
                <select
                  value={claimForm.issueType}
                  onChange={(e) => setClaimForm((f) => ({ ...f, issueType: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">Select…</option>
                  {ISSUE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={claimForm.description}
                  onChange={(e) => setClaimForm((f) => ({ ...f, description: e.target.value }))}
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="At least 20 characters…"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Photos (1–5 required)
                </label>
                <label className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-lg py-4 cursor-pointer hover:border-blue-400 text-sm text-gray-600">
                  <FaCamera />
                  Add photos
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => handlePhotoPick(e.target.files)}
                  />
                </label>
                {(claimForm.photos || []).length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {claimForm.photos.length} photo(s) attached
                  </p>
                )}
              </div>
              <button
                type="button"
                disabled={!canSubmitClaim || submitting}
                onClick={submitClaim}
                className="w-full py-3 rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-50"
              >
                {submitting ? 'Submitting…' : 'Submit warranty claim'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarrantyCards;
